import { parseQuarter } from './metricSeriesUtils';

export type MetricGoalEval = {
  operator?: string;
  target?: number;
  min?: number;
  max?: number;
  met?: boolean | null;
  delta?: number | null;
};

function readGoalEvalTarget(goalEval: MetricGoalEval | undefined): number | null {
  if (!goalEval) return null;
  if (typeof goalEval.target === 'number' && Number.isFinite(goalEval.target)) {
    return goalEval.target;
  }
  if (typeof goalEval.max === 'number' && Number.isFinite(goalEval.max)) {
    return goalEval.max;
  }
  if (typeof goalEval.min === 'number' && Number.isFinite(goalEval.min)) {
    return goalEval.min;
  }
  return null;
}

export function getGoalTargetsFromPoint(point: Record<string, unknown>): Record<string, number> {
  const goal = point.goal;
  if (!goal || typeof goal !== 'object') return {};

  const targets: Record<string, number> = {};
  for (const [fieldName, rawEval] of Object.entries(goal as Record<string, unknown>)) {
    if (!rawEval || typeof rawEval !== 'object') continue;
    const target = readGoalEvalTarget(rawEval as MetricGoalEval);
    if (target !== null) {
      targets[fieldName] = target;
    }
  }
  return targets;
}

export function readPointFieldGoal(
  point: Record<string, unknown>,
  fieldNames: string | string[]
): number | null {
  const keys = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
  const targets = getGoalTargetsFromPoint(point);
  for (const key of keys) {
    if (targets[key] !== undefined) return targets[key];
  }
  return null;
}

export function attachGoalValuesToChartData<T extends Record<string, unknown>>(
  chartData: T[],
  series: Record<string, unknown>[],
  valueKeys: string[]
): Array<T & Record<string, number | string>> {
  return chartData.map((row, index) => {
    const point = series[index];
    const next: Record<string, number | string> = {
      ...(row as Record<string, number | string>),
    };
    if (!point) return next as T & Record<string, number | string>;

    for (const key of valueKeys) {
      const target = readPointFieldGoal(point, key);
      if (target !== null) {
        next[`${key}__goal`] = target;
      }
    }
    return next as T & Record<string, number | string>;
  });
}

export function attachMappedGoalTargets<T extends Record<string, unknown>>(
  chartData: T[],
  series: Array<{ goalTargets?: Record<string, number | undefined> }>,
  mappings: Array<{ dataKey: string; goalField: string }>
): Array<T & Record<string, number | string>> {
  return chartData.map((row, index) => {
    const point = series[index];
    const next: Record<string, number | string> = {
      ...(row as Record<string, number | string>),
    };
    if (!point?.goalTargets) return next as T & Record<string, number | string>;

    for (const { dataKey, goalField } of mappings) {
      const target = point.goalTargets[goalField];
      if (typeof target === 'number' && Number.isFinite(target)) {
        next[`${dataKey}__goal`] = target;
      }
    }
    return next as T & Record<string, number | string>;
  });
}

export function collectGoalValues(
  chartData: Record<string, unknown>[],
  valueKeys: string[]
): number[] {
  const values: number[] = [];
  for (const key of valueKeys) {
    const goalKey = `${key}__goal`;
    for (const row of chartData) {
      const value = row[goalKey];
      if (typeof value === 'number' && Number.isFinite(value)) {
        values.push(value);
      }
    }
  }
  return values;
}

export function getGoalKeysWithValues(
  chartData: Record<string, unknown>[],
  valueKeys: string[]
): string[] {
  return valueKeys.filter((key) => {
    const goalKey = `${key}__goal`;
    return chartData.some(
      (row) => typeof row[goalKey] === 'number' && Number.isFinite(row[goalKey] as number)
    );
  });
}

/** Goals with the same target across all chart points — render as horizontal ReferenceLines. */
export function getConstantGoalLines(
  chartData: Record<string, unknown>[],
  valueKeys: string[]
): Record<string, number> {
  const constantGoals: Record<string, number> = {};

  for (const key of valueKeys) {
    const goalKey = `${key}__goal`;
    const targets = chartData
      .map((row) => row[goalKey])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const uniqueTargets = Array.from(new Set(targets));
    if (uniqueTargets.length === 1) {
      constantGoals[key] = uniqueTargets[0];
    }
  }

  return constantGoals;
}

/** Goals that change by quarter — render as Line series. */
export function getVaryingGoalKeys(
  chartData: Record<string, unknown>[],
  valueKeys: string[]
): string[] {
  const constantGoals = getConstantGoalLines(chartData, valueKeys);
  return getGoalKeysWithValues(chartData, valueKeys).filter((key) => constantGoals[key] === undefined);
}

/** Sum quarterly chart rows into annual totals for admin component project charts. */
export function aggregateQuarterlyChartToAnnual(
  chartData: Record<string, unknown>[],
  valueKeys: string[],
  quarterKey = 'quarter'
): Array<Record<string, number | string>> {
  const byYear = new Map<number, Record<string, number | string>>();

  for (const row of chartData) {
    const meta = parseQuarter(String(row[quarterKey] ?? ''));
    if (!meta) continue;

    const existing = byYear.get(meta.year) ?? {
      year: meta.year,
      yearLabel: String(meta.year),
    };

    for (const key of valueKeys) {
      const val = Number(row[key] ?? 0);
      if (Number.isFinite(val)) {
        existing[key] = Number(existing[key] ?? 0) + val;
      }
    }

    for (const key of valueKeys) {
      const goalKey = `${key}__goal`;
      const goalVal = row[goalKey];
      if (typeof goalVal === 'number' && Number.isFinite(goalVal)) {
        existing[goalKey] = goalVal;
      }
    }

    byYear.set(meta.year, existing);
  }

  return Array.from(byYear.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
}
